
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../../integrations/supabase/client';
import { Loader2, AlertCircle, TrendingUp, Calendar, Car, Users } from 'lucide-react';

interface DashboardStats {
  bookingsCount: number;
  vehiclesCount: number;
  profilesCount: number;
}

const fetchDashboardStats = async (): Promise<DashboardStats> => {
  try {
    // Fetch all stats in parallel
    const [bookingsResult, vehiclesResult, profilesResult] = await Promise.all([
      supabase.from('bookings').select('*', { count: 'exact', head: true }),
      supabase.from('vehicles').select('*', { count: 'exact', head: true }),
      supabase.from('profiles').select('*', { count: 'exact', head: true }),
    ]);

    // Log any errors for debugging
    if (bookingsResult.error) console.error('Bookings error:', bookingsResult.error);
    if (vehiclesResult.error) console.error('Vehicles error:', vehiclesResult.error);
    if (profilesResult.error) console.error('Profiles error:', profilesResult.error);

    return {
      bookingsCount: bookingsResult.count || 0,
      vehiclesCount: vehiclesResult.count || 0,
      profilesCount: profilesResult.count || 0,
    };
  } catch (error) {
    console.error('Dashboard stats error:', error);
    throw error;
  }
};

const DashboardHome = () => {
  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['dashboardStats'],
    queryFn: fetchDashboardStats,
    retry: 1,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-2">Loading dashboard data...</span>
      </div>
    );
  }

  if (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 mb-6">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          <h3 className="text-lg font-medium">Dashboard Error</h3>
        </div>
        <p className="mt-1">There was a problem loading the dashboard data.</p>
        <p className="mt-2 text-sm font-mono bg-red-100 p-2 rounded">
          {errorMessage}
        </p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Dashboard Overview</h2>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold mb-2">Total Bookings</h3>
            <Calendar className="w-6 h-6 text-blue-500" />
          </div>
          <p className="text-3xl font-bold">{stats?.bookingsCount || 0}</p>
          <p className="text-sm text-gray-500 mt-2">All time bookings</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold mb-2">Active Vehicles</h3>
            <Car className="w-6 h-6 text-green-500" />
          </div>
          <p className="text-3xl font-bold">{stats?.vehiclesCount || 0}</p>
          <p className="text-sm text-gray-500 mt-2">Available for booking</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold mb-2">Total Customers</h3>
            <Users className="w-6 h-6 text-purple-500" />
          </div>
          <p className="text-3xl font-bold">{stats?.profilesCount || 0}</p>
          <p className="text-sm text-gray-500 mt-2">Registered users</p>
        </div>
      </div>

      {/* Welcome Message */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center mb-4">
          <TrendingUp className="w-6 h-6 text-primary mr-2" />
          <h3 className="text-lg font-semibold">Welcome to Your Admin Dashboard</h3>
        </div>
        <p className="text-gray-600">
          Monitor your chauffeur service business with real-time insights. Manage bookings, vehicles, and customers all in one place.
        </p>
      </div>
    </div>
  );
};

export default DashboardHome;
